import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Alert, AlertDescription } from './components/ui/alert';
import { CheckCircle, XCircle, Clock, AlertTriangle, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';

const RegistrationResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed, error
  const [userInfo, setUserInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        setErrorMessage('No session ID provided. Please try registering again.');
        return;
      }

      try {
        console.log('🔍 Checking registration status for session:', sessionId);
        
        // Check registration status with the backend
        const response = await axios.get(`${API}/auth/registration-status/${sessionId}`);
        
        if (response.status === 200) {
          const data = response.data;
          console.log('📋 Registration status response:', data);
          
          setSessionInfo({
            session_id: sessionId,
            payment_status: data.payment_status,
            user_created: data.user_created
          });

          if (data.user_created && data.user_info) {
            // Registration completed successfully
            setStatus('success');
            setUserInfo(data.user_info);
          } else if (data.payment_status === 'paid' && !data.user_created) {
            // Payment succeeded but user creation failed
            setStatus('error');
            setErrorMessage('Payment was processed successfully, but there was an issue creating your account. Please contact support with your payment confirmation.');
          } else if (data.payment_status === 'pending') {
            // Payment still processing
            setStatus('checking');
            // Check again more frequently for better UX
            setTimeout(checkRegistrationStatus, 1500);
          } else {
            // Payment failed or other issue
            setStatus('failed');
            setErrorMessage(data.error_message || 'Completing your registration... This usually takes a few moments.');
          }
        }
      } catch (error) {
        console.error('❌ Error checking registration status:', error);
        
        if (error.response?.status === 404) {
          setStatus('error');
          setErrorMessage('Session not found. The registration link may be invalid or expired.');
        } else {
          setStatus('error');
          setErrorMessage('Unable to verify registration status. Please contact support if you completed payment.');
        }
      }
    };

    checkRegistrationStatus();
  }, [sessionId]);

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <Card className="w-full max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Clock className="h-16 w-16 text-blue-500 animate-pulse" />
              </div>
              <CardTitle className="text-2xl text-blue-600">
                Completing Your Registration
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-lg text-slate-600">
                Your payment was successful! We're setting up your account and membership...
              </p>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
              <div className="text-sm text-slate-500 space-y-1">
                <p>✓ Payment confirmed</p>
                <p>🔄 Creating your account...</p>
                <p className="text-xs">This usually takes just a few seconds.</p>
              </div>
            </CardContent>
          </Card>
        );

      case 'success':
        return (
          <Card className="w-full max-w-2xl mx-auto border-green-200">
            <CardHeader className="text-center">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <CardTitle className="text-2xl text-green-600">
                Welcome to ACE Member's Portal 🎉
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription className="text-green-800">
                  <strong>Registration Successful!</strong> Your account has been created and verified.
                </AlertDescription>
              </Alert>

              {userInfo && (
                <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                  <h3 className="font-semibold text-slate-800 text-lg">Account Details:</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {userInfo.first_name_1} {userInfo.first_name_2 ? userInfo.first_name_2 + ' ' : ''}{userInfo.last_name}</p>
                    <p><strong>Email:</strong> {userInfo.email}</p>
                    <p><strong>Member Category:</strong> {userInfo.member_category}</p>
                    <p className="flex items-center gap-2">
                      <strong>Status:</strong> 
                      <span className="text-green-600 flex items-center gap-1 font-medium">
                        Verified <CheckCircle className="h-4 w-4" />
                      </span>
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <h3 className="font-semibold text-slate-800 text-lg">What's Next?</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Your €35 annual membership fee has been processed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You can now book cultural events and access the library and mediateca catalog</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Check your email for a welcome message with additional information</span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-center">
                <Button 
                  onClick={() => navigate('/')} 
                  className="w-full max-w-md bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  Browse the ACE Portal
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'failed':
        return (
          <Card className="w-full max-w-2xl mx-auto border-red-200">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <XCircle className="h-16 w-16 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-red-600">
                Registration Not Completed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-red-200 bg-red-50">
                <XCircle className="h-4 w-4" />
                <AlertDescription className="text-red-800">
                  <strong>Registration Failed:</strong> {errorMessage}
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <h3 className="font-semibold text-slate-800">What Can You Do?</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <ArrowLeft className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Try registering again with the same or different email</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Contact support if you were charged but registration failed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Check if you already have an account and try signing in</span>
                  </li>
                </ul>
              </div>

              {sessionInfo && (
                <div className="bg-slate-50 p-4 rounded-lg text-sm">
                  <h4 className="font-semibold text-slate-800 mb-2">Session Information:</h4>
                  <p><strong>Session ID:</strong> {sessionInfo.session_id}</p>
                  <p><strong>Payment Status:</strong> {sessionInfo.payment_status}</p>
                  <p className="text-xs text-slate-500 mt-2">
                    Please provide this information if you contact support.
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  onClick={() => navigate('/')} 
                  className="flex-1"
                >
                  Try Registration Again
                </Button>
                <Button 
                  onClick={() => navigate('/?tab=signin')} 
                  variant="outline"
                  className="flex-1"
                >
                  Sign In Instead
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'error':
        return (
          <Card className="w-full max-w-2xl mx-auto border-orange-200">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="h-16 w-16 text-orange-500" />
              </div>
              <CardTitle className="text-2xl text-orange-600">
                Unable to Verify Registration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-orange-800">
                  <strong>Verification Issue:</strong> {errorMessage}
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <h3 className="font-semibold text-slate-800">Recommended Actions:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Try signing in with your email - your account may already exist</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Check your email for registration confirmation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span>Contact support if you completed payment but can't access your account</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Support Information:</h4>
                <p className="text-sm text-blue-700">
                  Email: <a href="mailto:info@associazione-culturale-europea.eu" className="underline">
                    info@associazione-culturale-europea.eu
                  </a>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Include session ID: {sessionId}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  onClick={() => navigate('/?tab=signin')} 
                  className="flex-1"
                >
                  Try Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/')} 
                  variant="outline"
                  className="flex-1"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {renderContent()}
        
        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500">
            ACE Library - Associazione Culturale Europea
          </p>
          <p className="text-xs text-slate-400 mt-1">
            European Cultural Association
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationResult;